import "./Scouting.css";

import {
  fetchScoutingFeatures,
  resetScouting,
  setScoutingDates,
  setSelectedScoutingFeature,
} from "../../../../actions/resultMap";
import { format, parseISO } from "date-fns";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo, useState } from "react";

import { Badge } from "reactstrap";
import { ScoutingFeature } from "./ScoutingFeature";
import { addDangerAlert } from "../../../../actions/alerts";
import { requestFetchScoutingDates } from "../../../../services/backendRequests";

// The endpoint returns one timestamp per photo, so a site with a single
// scouting run comes back as fifty identical days. Grouped by day, that is the
// whole list of what can be picked: a calendar showed six weeks of empty cells
// around it, let you select days holding nothing, and made you page through
// months to reach the one day that does.
const groupByDay = (datetimes) =>
  datetimes.reduce((acc, datetime) => {
    const day = format(new Date(datetime), "yyyy-MM-dd");
    return { ...acc, [day]: (acc[day] ?? 0) + 1 };
  }, {});

export const Scouting = () => {
  const dispatch = useDispatch();

  const user = useSelector(({ user }) => user);
  // The ticked days live in the store, not here: the panel is unmounted every
  // time the layer control closes, and the bottom bar can clear them.
  const [scoutingFeatures, selectedDays, siteId] = useSelector(
    ({ resultMap }) => [
      resultMap.scoutingFeatures,
      resultMap.scoutingDays,
      resultMap.trial.id,
    ]
  );

  const [dayCounts, setDayCounts] = useState({});

  const days = useMemo(
    () => Object.keys(dayCounts).sort((a, b) => b.localeCompare(a)),
    [dayCounts]
  );

  useEffect(() => {
    // The dates of the site we just left must go on the spot, or they stay
    // listed, and pickable, until the new answer lands. The flag then drops
    // that late answer so it cannot overwrite the new site's dates.
    setDayCounts({});
    let currentSite = true;

    requestFetchScoutingDates(user, siteId)
      .then((dates) => currentSite && setDayCounts(groupByDay(dates)))
      .catch((err) => {
        if (!currentSite) return;
        setDayCounts({});
        dispatch(addDangerAlert(err));
      });

    return () => {
      currentSite = false;
    };
  }, [dispatch, user, siteId]);

  const toggleDay = (day) => {
    const next = selectedDays.includes(day)
      ? selectedDays.filter((selected) => selected !== day)
      : [...selectedDays, day];

    if (next.length === 0) {
      dispatch(resetScouting());
      return;
    }

    // The endpoint takes an interval, so it gets the envelope of the ticked
    // days and the action drops the days in between.
    const sorted = [...next].sort();
    const range = [
      new Date(`${sorted[0]}T00:00:00`),
      new Date(`${sorted[sorted.length - 1]}T23:59:59.999`),
    ];
    dispatch(setScoutingDates(range, next));
    // Any feature selected on the map may be about to disappear from the set.
    dispatch(setSelectedScoutingFeature(null));
    dispatch(fetchScoutingFeatures(range, next));
  };

  return (
    <div className="d-flex scouting">
      <div className="scouting-dates">
        <span className="scouting-dates-title">
          <i className="fa-solid fa-camera me-1" />
          Scouting dates ({days.length})
        </span>
        {days.length === 0 ? (
          <span className="font-style-italic p-2">
            No scouting images on this site
          </span>
        ) : (
          <div className="scouting-dates-list scrollbar-light">
            {days.map((day) => (
              <Badge
                key={day}
                // Same pill as the scouting traits of the bottom bar: one
                // language for everything scouting (ScoutingBadge.scss).
                className={`scouting-trait-badge elevated clickable d-flex align-items-center justify-content-between gap-2 ${
                  selectedDays.includes(day) ? "active" : ""
                }`}
                onClick={() => toggleDay(day)}
              >
                <span>{format(parseISO(day), "EE. d MMM yyyy")}</span>
                <span className="scouting-dates-count">
                  <i className="fa-solid fa-camera me-1" />
                  {dayCounts[day]}
                </span>
              </Badge>
            ))}
          </div>
        )}
      </div>
      {scoutingFeatures.length === 0 ? (
        <div className="w-100 d-flex align-items-center text-center justify-content-center">
          {selectedDays.length === 0
            ? "Pick one or more dates to display scouting images"
            : "No scouting images found on the selected dates"}
        </div>
      ) : (
        <div className="grid-wrapper p-2 gap-2">
          {scoutingFeatures.map((feature) => (
            <ScoutingFeature key={feature.id} scoutingFeature={feature} />
          ))}
        </div>
      )}
    </div>
  );
};
